import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  MessageCircle,
  Settings,
  Send,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Clock,
  Shield,
  Key
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getWhatsAppAdminSettings,
  updateWhatsAppAdminSettings,
  getWhatsAppConfigStatus,
  getWhatsAppLogs,
  sendWhatsAppTestMessage
} from '../lib/api';

const AdminWhatsApp = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ enabled: false });
  const [configStatus, setConfigStatus] = useState({ token_configured: false, token_status: 'Not set', phone_id_configured: false });
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, configRes, logsRes] = await Promise.all([
        getWhatsAppAdminSettings(),
        getWhatsAppConfigStatus(),
        getWhatsAppLogs(50)
      ]);
      
      setSettings(settingsRes.data);
      setConfigStatus(configRes.data);
      setLogs(logsRes.data.logs || []);
      setLogsTotal(logsRes.data.total || 0);
    } catch (error) {
      console.error('Error fetching WhatsApp admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load WhatsApp settings');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleChange = (checked) => {
    if (checked) {
      // Show confirmation dialog before turning ON
      setPendingEnabled(true);
      setShowConfirmDialog(true);
    } else {
      // Turn OFF directly without confirmation
      updateSettings(false);
    }
  };

  const updateSettings = async (enabled) => {
    setActionLoading(true);
    try {
      await updateWhatsAppAdminSettings(enabled);
      setSettings(prev => ({ ...prev, enabled }));
      toast.success(enabled ? 'WhatsApp notifications enabled' : 'WhatsApp notifications disabled');
    } catch (error) {
      console.error('Error updating WhatsApp settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setActionLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const handleConfirmEnable = () => {
    updateSettings(true);
  };

  const handleSendTestMessage = async () => {
    setTestLoading(true);
    try {
      const response = await sendWhatsAppTestMessage();
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh logs to show the test message
        const logsRes = await getWhatsAppLogs(50);
        setLogs(logsRes.data.logs || []);
        setLogsTotal(logsRes.data.total || 0);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to send test message');
    } finally {
      setTestLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Sent</Badge>;
      case 'delivered':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Delivered</Badge>;
      case 'skipped':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Skipped</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-green-500" />
              WhatsApp Notifications
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage WhatsApp notification settings for opted-in users
            </p>
          </div>
          <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="open-whatsapp-inbox-btn">
            <Link to="/admin/whatsapp/inbox">Open Inbox</Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          </div>
        </div>

        {/* Settings Card */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Master Switch
              </CardTitle>
              <CardDescription>
                Enable or disable WhatsApp notifications for all opted-in users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">WhatsApp Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    {settings.enabled ? 'Sending messages to opted-in users' : 'All messages are disabled'}
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={handleToggleChange}
                  disabled={actionLoading}
                  data-testid="whatsapp-master-switch"
                />
              </div>
              
              {settings.updated_at && (
                <p className="text-xs text-muted-foreground">
                  Last updated: {formatTimestamp(settings.updated_at)} by {settings.updated_by || 'system'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Configuration Status
              </CardTitle>
              <CardDescription>
                Environment variables required for WhatsApp API
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <span className="text-sm">Access Token</span>
                      {configStatus.token_status && (
                        <p className="text-xs text-muted-foreground">{configStatus.token_status}</p>
                      )}
                    </div>
                  </div>
                  {configStatus.token_configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Valid
                    </Badge>
                  ) : configStatus.token_status && configStatus.token_status !== 'Not set' ? (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Invalid
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <XCircle className="w-3 h-3 mr-1" />
                      Not Set
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Phone Number ID</span>
                  </div>
                  {configStatus.phone_id_configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Configured
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <XCircle className="w-3 h-3 mr-1" />
                      Not Set
                    </Badge>
                  )}
                </div>
              </div>
              
              {(!configStatus.token_configured || !configStatus.phone_id_configured) && (
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                  <p className="text-xs text-amber-400">
                    WhatsApp credentials must be set in the deployment environment variables before enabling notifications.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Test Message Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Test Message
            </CardTitle>
            <CardDescription>
              Send a test notification to your own WhatsApp number
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm">
                  Sends a "deposit_received" template message with test amount (USD 0.01) to your saved WhatsApp number.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  You must be opted in to WhatsApp notifications in Settings &gt; Preferences to receive the test.
                </p>
              </div>
              <Button
                onClick={handleSendTestMessage}
                disabled={testLoading || !configStatus.token_configured}
                data-testid="send-test-message-btn"
              >
                {testLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test to Me
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Message Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Message Logs
            </CardTitle>
            <CardDescription>
              Last 50 WhatsApp message attempts ({logsTotal} total)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No WhatsApp messages have been sent yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Error Code</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatTimestamp(log.ts)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.user_email_masked || log.user_id?.slice(0, 8) + '...'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {log.template}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(log.status)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.error_code || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirm Enable Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enable WhatsApp Notifications?</DialogTitle>
              <DialogDescription>
                Turn on WhatsApp messages to opted-in users? Messages will be sent for deposits, transfers, and payment requests.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmEnable}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Yes, Enable
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminWhatsApp;
